import React from 'react';
import PropTypes from 'prop-types';
import { Table as AntTable } from 'antd';
import './Table.scss';

/**
 * Design System Table Component
 *
 * A flexible table wrapper around Ant Design Table with standardized styling variants.
 * Supports both bordered and borderless modes for different contexts.
 *
 * @example
 * // Financial table with borders (default)
 * <Table variant="financial" columns={columns} dataSource={data} />
 *
 * @example
 * // Financial table without borders
 * <Table variant="financial" borderless columns={columns} dataSource={data} />
 *
 * @example
 * // Custom styling with className
 * <Table variant="custom" className="my-table" columns={columns} dataSource={data} />
 */
const Table = ({
    variant = 'financial',
    bordered,
    borderless = false,
    roundedCorners = true,
    className = '',
    scroll,
    ...props
}) => {
    // Determine bordered state
    // Priority: borderless prop > bordered prop > variant default
    let isBordered;
    if (borderless) {
        isBordered = false;
    } else if (bordered !== undefined) {
        isBordered = bordered;
    } else {
        // Default based on variant
        isBordered = variant === 'financial' || variant === 'matrix';
    }

    // Build class names
    const variantClass = variant !== 'custom' ? `ds-table-${variant}` : '';
    const borderedClass = isBordered ? 'ds-table-bordered' : 'ds-table-borderless';
    const roundedClass = roundedCorners ? 'ds-table-rounded' : '';
    const combinedClassName = `ds-table ${variantClass} ${borderedClass} ${roundedClass} ${className}`.trim();

    // Default scroll behavior for financial and matrix variants
    const defaultScroll = (variant === 'financial' || variant === 'matrix')
        ? { x: 'max-content', ...scroll }
        : scroll;

    return (
        <AntTable
            className={combinedClassName}
            bordered={isBordered}
            scroll={defaultScroll}
            {...props}
        />
    );
};

Table.propTypes = {
    /** Table styling variant */
    variant: PropTypes.oneOf(['financial', 'standard', 'matrix', 'custom']),

    /** Show borders around table cells (auto-determined by variant if not specified) */
    bordered: PropTypes.bool,

    /** Remove borders (alternative to bordered={false}) */
    borderless: PropTypes.bool,

    /** Apply rounded corners to table */
    roundedCorners: PropTypes.bool,

    /** Additional CSS classes */
    className: PropTypes.string,

    /** Table columns configuration */
    columns: PropTypes.array.isRequired,

    /** Table data source */
    dataSource: PropTypes.array,

    /** Scroll configuration */
    scroll: PropTypes.object,

    /** All other Ant Design Table props */
};

export default Table;
